// gallery.jsx — mirrors gallery-upload.html exactly
// Photographer portal: OTP login (photographer role), select event, drag-drop upload
import { useState, useRef, useCallback } from "react";
import api, { setToken } from "@/lib/api";

export default function Gallery() {
  const [step,    setStep]    = useState("login"); // login | events | upload
  const [email,   setEmail]   = useState("");
  const [code,    setCode]    = useState("");
  const [msg,     setMsg]     = useState("");
  const [loginStep, setLoginStep] = useState("email"); // email | code
  const [events,  setEvents]  = useState([]);
  const [selEv,   setSelEv]   = useState(null);
  const [photos,  setPhotos]  = useState([]); // [{file, preview, caption, status}]
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const inputRef = useRef();

  const authToken = localStorage.getItem("galleryToken") || "";

  const sendOtp = async () => {
    if (!email.trim()) { setMsg("Enter your email."); return; }
    const d = await api.photographerLogin(email.trim().toLowerCase(), "", "requestOtp").catch(() => null);
    if (d?.success) { setLoginStep("code"); setMsg(d.message || "Code sent."); }
    else setMsg(d?.message || "Failed to send.");
  };

  const verifyOtp = async () => {
    if (!code.trim()) { setMsg("Enter the 6-digit code."); return; }
    const d = await api.photographerLogin(email.trim().toLowerCase(), code.trim(), "verifyOtp").catch(() => null);
    if (d?.success && d?.token) {
      setToken(d.token);                       // store where the API client reads it
      localStorage.setItem("galleryEmail", d.email);
      setStep("events");
      loadEvents(d.token);
    } else setMsg(d?.message || "Invalid code or not a photographer account.");
  };

  const loadEvents = async (token) => {
    const d = await api.myGalleryEvents().catch(() => null);
    if (d?.success) {
      setEvents(d.events || []);
      if ((d.events || []).length === 0) setMsg("No events assigned to you.");
    }
  };

  const selectEvent = (ev) => { setSelEv(ev); setStep("upload"); setPhotos([]); setSubmitStatus(""); };

  const handleFiles = (fileList) => {
    const newPhotos = [];
    Array.from(fileList).forEach(file => {
      if (!file.type.startsWith("image/")) { alert(`${file.name} is not an image.`); return; }
      if (file.size > 8 * 1024 * 1024) { alert(`${file.name} is too large (max 8 MB).`); return; }
      const reader = new FileReader();
      reader.onload = e => {
        setPhotos(p => [...p, { file, preview: e.target.result, caption: "", status: "ready" }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (idx) => setPhotos(p => p.filter((_, i) => i !== idx));
  const setCaption  = (idx, v) => setPhotos(p => p.map((ph, i) => i === idx ? { ...ph, caption: v } : ph));

  const submitPhotos = async () => {
    if (!photos.length) { alert("Add at least one photo."); return; }
    setSubmitting(true);
    const bookingID = selEv.bookingID || selEv.booking_id;
    const uploaded = [];
    let ok = 0, fail = 0;
    for (let i = 0; i < photos.length; i++) {
      const ph = photos[i];
      if (ph.status === "done") continue;
      setPhotos(p => p.map((x, j) => j === i ? { ...x, status: "uploading" } : x));
      try {
        const base64 = ph.preview.split(",")[1];
        // Relay uploads to a PRIVATE "Pending Review" folder and returns { fileId, url:'pending_review' }
        const r = await api.uploadGalleryPhoto({
          bookingID,
          fileName:  ph.file.name,
          mimeType:  ph.file.type,
          data:      base64,
          caption:   ph.caption,
          uploaderEmail: localStorage.getItem("galleryEmail") || "",
        });
        const driveFileId = r?.fileId || r?.driveFileId || r?.id;
        if (!driveFileId) throw new Error(r?.message || "Upload did not return a file ID");
        uploaded.push({ driveFileId, publicUrl: "", thumbUrl: "", caption: ph.caption || "" });
        setPhotos(p => p.map((x, j) => j === i ? { ...x, status: "done" } : x));
        ok++;
      } catch (e) {
        setPhotos(p => p.map((x, j) => j === i ? { ...x, status: "error", err: e.message } : x));
        fail++;
      }
    }
    // Record the uploaded photos in the DB so they appear in the admin review queue
    if (uploaded.length) {
      try {
        await api.submitGalleryPhotos(bookingID, uploaded);
      } catch (e) {
        setSubmitting(false);
        setSubmitStatus("Photos uploaded but couldn't be sent for review: " + e.message);
        return;
      }
    }
    setSubmitting(false);
    if (ok > 0 && fail === 0) {
      setSubmitStatus(`✓ ${ok} photo${ok > 1 ? "s" : ""} sent for admin review. They go public once approved.`);
    } else if (fail > 0) {
      setSubmitStatus(`${ok} uploaded, ${fail} failed.`);
    }
  };

  const S = {
    body: { fontFamily:"'Inter',system-ui,sans-serif", background:"#0f172a", color:"#f1f5f9", minHeight:"100vh", fontSize:14 },
    topbar: { background:"#1e293b", borderBottom:"1px solid #334155", padding:"0 16px", height:52, display:"flex", alignItems:"center", justifyContent:"space-between" },
    card: { background:"#1e293b", borderRadius:12, padding:20, margin:16 },
    label: { fontSize:11, color:"#94a3b8", marginBottom:4, display:"block" },
    inp: { width:"100%", background:"#0f172a", border:"1px solid #334155", borderRadius:8, padding:"9px 12px", fontSize:13, color:"#f1f5f9", fontFamily:"inherit", boxSizing:"border-box", marginBottom:12 },
    btn: (color="#3b82f6") => ({ background:color, color:"#fff", border:"none", borderRadius:8, padding:"10px 20px", fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"inherit", width:"100%" }),
    evCard: (sel) => ({ background:sel?"rgba(59,130,246,.15)":"#0f172a", border:`1px solid ${sel?"#3b82f6":"#334155"}`, borderRadius:12, padding:14, marginBottom:10, cursor:"pointer" }),
    photoPill: (status) => ({
      width:"100%", paddingTop:"100%", position:"relative", borderRadius:10, overflow:"hidden", background:"#0f172a",
      border: `2px solid ${status==="done"?"#10b981":status==="error"?"#ef4444":status==="uploading"?"#f59e0b":"#334155"}`
    }),
  };

  if (step === "login") return (
    <div style={S.body}>
      <div style={S.topbar}>
        <div style={{ fontWeight:700 }}>📷 BMSIT Gallery Upload</div>
      </div>
      <div style={{ maxWidth:380, margin:"32px auto 0", padding:"0 16px" }}>
        <div style={S.card}>
          <div style={{ fontWeight:700, marginBottom:4 }}>Photographer login</div>
          <div style={{ fontSize:12, color:"#94a3b8", marginBottom:18 }}>Sign in with your photographer-role account.</div>
          {loginStep === "email" ? (
            <>
              <label style={S.label}>Email</label>
              <input style={S.inp} type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendOtp()} placeholder="your@bmsit.in" autoFocus />
              <button style={S.btn()} onClick={sendOtp}>Send login code</button>
            </>
          ) : (
            <>
              <div style={{ fontSize:12, color:"#94a3b8", marginBottom:12 }}>Code sent to {email}</div>
              <label style={S.label}>6-digit code</label>
              <input style={S.inp} type="text" inputMode="numeric" maxLength={6} value={code} onChange={e=>setCode(e.target.value)} onKeyDown={e=>e.key==="Enter"&&verifyOtp()} placeholder="000000" autoFocus />
              <button style={S.btn()} onClick={verifyOtp}>Verify & Sign In</button>
              <button style={{ ...S.btn("#334155"), marginTop:8 }} onClick={()=>setLoginStep("email")}>← Back</button>
            </>
          )}
          {msg && <div style={{ marginTop:10, fontSize:12, color:msg.includes("sent")||msg.includes("✓")?"#10b981":"#ef4444" }}>{msg}</div>}
        </div>
      </div>
    </div>
  );

  if (step === "events") return (
    <div style={S.body}>
      <div style={S.topbar}>
        <div style={{ fontWeight:700 }}>📷 Select event</div>
        <button onClick={()=>{setToken(null);localStorage.removeItem("galleryEmail");setStep("login");}} style={{ background:"#334155", border:"none", color:"#94a3b8", borderRadius:8, padding:"5px 12px", cursor:"pointer", fontSize:12 }}>Sign out</button>
      </div>
      <div style={{ padding:16 }}>
        {events.length === 0 ? (
          <div style={{ textAlign:"center", padding:40, color:"#64748b" }}>No events assigned to you.</div>
        ) : events.map(ev => (
          <div key={ev.bookingID || ev.booking_id} style={S.evCard(false)} onClick={()=>selectEvent(ev)}>
            <div style={{ fontWeight:600, marginBottom:4 }}>{ev.title || ev.bookingID || ev.booking_id}</div>
            <div style={{ fontSize:12, color:"#94a3b8" }}>{ev.date} · {ev.photo_count||ev.photoCount||0} photos uploaded</div>
          </div>
        ))}
        {msg && <div style={{ fontSize:13, color:"#94a3b8", textAlign:"center" }}>{msg}</div>}
      </div>
    </div>
  );

  // Upload step
  return (
    <div style={S.body}>
      <div style={S.topbar}>
        <div>
          <div style={{ fontWeight:700 }}>📷 Upload photos</div>
          <div style={{ fontSize:10, color:"#94a3b8" }}>{selEv?.title}</div>
        </div>
        <button onClick={()=>setStep("events")} style={{ background:"#334155", border:"none", color:"#94a3b8", borderRadius:8, padding:"5px 12px", cursor:"pointer", fontSize:12 }}>← Back</button>
      </div>

      <div style={{ padding:16 }}>
        {/* Drop zone */}
        <div
          style={{ border:"2px dashed #334155", borderRadius:12, padding:28, textAlign:"center", cursor:"pointer", marginBottom:16 }}
          onClick={()=>inputRef.current?.click()}
          onDragOver={e=>e.preventDefault()}
          onDrop={e=>{e.preventDefault();handleFiles(e.dataTransfer.files);}}
        >
          <div style={{ fontSize:32, marginBottom:8 }}>📁</div>
          <div style={{ fontWeight:500, marginBottom:4 }}>Tap or drag photos here</div>
          <div style={{ fontSize:11, color:"#94a3b8" }}>JPG, PNG, WEBP · max 8 MB each</div>
          <input ref={inputRef} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={e=>handleFiles(e.target.files)} />
        </div>

        {/* Photo grid */}
        {photos.length > 0 && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:8, marginBottom:16 }}>
              {photos.map((ph, i) => (
                <div key={i} style={{ position:"relative" }}>
                  <div style={{ width:"100%", paddingTop:"100%", position:"relative", borderRadius:10, overflow:"hidden", background:"#0f172a", border:`2px solid ${ph.status==="done"?"#10b981":ph.status==="error"?"#ef4444":ph.status==="uploading"?"#f59e0b":"#334155"}` }}>
                    <img src={ph.preview} alt="" style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }} />
                    {ph.status==="uploading" && <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.5)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>⏳</div>}
                    {ph.status==="done"      && <div style={{ position:"absolute", inset:0, background:"rgba(16,185,129,.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>✓</div>}
                    {ph.status==="error"     && <div style={{ position:"absolute", inset:0, background:"rgba(239,68,68,.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>✗</div>}
                    {ph.status==="ready" && (
                      <button onClick={()=>removePhoto(i)} style={{ position:"absolute", top:4, right:4, background:"rgba(0,0,0,.7)", border:"none", color:"#fff", borderRadius:"50%", width:22, height:22, cursor:"pointer", fontSize:12, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                    )}
                  </div>
                  <input
                    type="text" value={ph.caption} onChange={e=>setCaption(i,e.target.value)}
                    placeholder="Caption…"
                    style={{ width:"100%", marginTop:4, background:"#1e293b", border:"1px solid #334155", borderRadius:6, padding:"4px 6px", fontSize:10, color:"#f1f5f9", fontFamily:"inherit", boxSizing:"border-box" }}
                  />
                </div>
              ))}
            </div>
            {submitStatus && (
              <div style={{ padding:"10px 14px", borderRadius:8, marginBottom:12, background:submitStatus.startsWith("✓")?"rgba(16,185,129,.15)":"rgba(245,158,11,.15)", color:submitStatus.startsWith("✓")?"#10b981":"#f59e0b", fontSize:13 }}>
                {submitStatus}
              </div>
            )}
            <button style={S.btn(submitting?"#334155":"#3b82f6")} onClick={submitPhotos} disabled={submitting}>
              {submitting ? "Uploading…" : `Submit ${photos.filter(p=>p.status==="ready").length} photo${photos.length!==1?"s":""} for review`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
